<template>
  <v-data-table
    v-if="data.length > 0"
    :items="data"
    :headers="headers"
    :mobile-breakpoint="0"
    :items-per-page="10"
    hide-default-footer
    sort-by="date"
    :sort-desc="true"
    :page.sync="page"
    @page-count="pageCount = $event"
  >
    <template v-slot:item.date="{ item }">
      <span>{{ getDateDisplay(item.date) }}</span>
    </template>
    <template v-slot:item.sales="{ item }">
      <span class="font-weight-bold">{{
        item.sales !== null ? item.sales : ""
      }}</span>
      <span v-if="item.sales !== null" class="caption font-weight-bold ml-1"
        >$</span
      >
    </template>
    <template v-slot:item.net_sales="{ item }">
      <span>{{ item.net_sales !== null ? item.net_sales : "" }}</span>
      <span v-if="item.net_sales !== null" class="caption ml-1">$</span>
      <span v-if="item.net_sales !== null" class="caption ml-1"
        >({{ getNetRatio(item) }}%)</span
      >
    </template>
    <template v-slot:item.cost="{ item }">
      <span class="font-weight-bold">{{
        item.cost !== null ? item.cost : ""
      }}</span>
      <span v-if="item.cost !== null" class="caption font-weight-bold ml-1"
        >$</span
      >
      <span
        v-if="item.cost !== null && item.sales !== 0 && item.sales !== null"
        class="caption font-weight-bold ml-1"
        >({{ getPercentCost(item) }}%)</span
      >
    </template>
    <template v-slot:item.past_year_sales="{ item }">
      <v-row
        justify="space-between"
        no-gutters
        v-if="item.past_year_sales !== null"
      >
        <div>
          <span>{{ item.past_year_sales }}</span>
          <span class="caption ml-1">$</span>
        </div>
        <v-row
          no-gutters
          justify="end"
          v-if="getDelta(item.sales, item.past_year_sales) !== null"
        >
          <v-icon
            small
            left
            color="success"
            v-if="getDelta(item.sales, item.past_year_sales) > 0"
          >
            fa-chevron-up
          </v-icon>
          <v-icon small left color="error" v-else>
            fa-chevron-down
          </v-icon>
          <span
            class="caption"
            :style="{
              color:
                getDelta(item.sales, item.past_year_sales) > 0
                  ? '#149911'
                  : '#DE1A1A'
            }"
          >
            {{ Math.abs(getDelta(item.sales, item.past_year_sales)) }} %</span
          >
        </v-row>
      </v-row>
    </template>
    <template v-slot:item.last_week_sales="{ item }">
      <v-row
        justify="space-between"
        no-gutters
        v-if="item.last_week_sales !== null"
      >
        <div>
          <span>{{ item.last_week_sales }}</span>
          <span class="caption ml-1">$</span>
        </div>
        <v-row
          no-gutters
          justify="end"
          v-if="getDelta(item.sales, item.last_week_sales) !== null"
        >
          <v-icon
            small
            left
            color="success"
            v-if="getDelta(item.sales, item.last_week_sales) > 0"
          >
            fa-chevron-up
          </v-icon>
          <v-icon small left color="error" v-else>
            fa-chevron-down
          </v-icon>
          <span
            class="caption"
            :style="{
              color:
                getDelta(item.sales, item.last_week_sales) > 0
                  ? '#149911'
                  : '#DE1A1A'
            }"
          >
            {{ Math.abs(getDelta(item.sales, item.last_week_sales)) }} %</span
          >
        </v-row>
      </v-row>
    </template>
    <template v-slot:item.yesterday_sales="{ item }">
      <v-row
        justify="space-between"
        no-gutters
        v-if="item.yesterday_sales !== null"
      >
        <div>
          <span>{{ item.yesterday_sales }}</span>
          <span class="caption ml-1">$</span>
        </div>
        <v-row
          no-gutters
          justify="end"
          v-if="getDelta(item.sales, item.yesterday_sales) !== null"
        >
          <v-icon
            small
            left
            color="success"
            v-if="getDelta(item.sales, item.yesterday_sales) > 0"
          >
            fa-chevron-up
          </v-icon>
          <v-icon small left color="error" v-else>
            fa-chevron-down
          </v-icon>
          <span
            class="caption"
            :style="{
              color:
                getDelta(item.sales, item.yesterday_sales) > 0
                  ? '#149911'
                  : '#DE1A1A'
            }"
          >
            {{ Math.abs(getDelta(item.sales, item.yesterday_sales)) }} %</span
          >
        </v-row>
      </v-row>
    </template>
    <template v-slot:item.actions="{ item }">
      <v-row
        dense
        no-gutters
        align="center"
        justify="end"
        v-if="item.sale_id !== null"
      >
        <v-tooltip left>
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" small icon @click="updateCost(item)">
              <v-icon small>fa-sync</v-icon>
            </v-btn>
          </template>
          Recalculer le cost
        </v-tooltip>

        <SalesEditor
          :id="item.sale_id"
          @update="$emit('update')"
          @delete="$emit('update')"
        ></SalesEditor>
      </v-row>
    </template>
    <template v-slot:footer>
      <v-pagination
        color="accent"
        v-model="page"
        :length="pageCount"
        :total-visible="5"
      ></v-pagination>
    </template>
  </v-data-table>
  <v-row v-else align="center" justify="center">
    <v-icon small class="mr-1">fa-exclamation-circle</v-icon>
    <span>Pas de données pour la période sélectionnée</span>
  </v-row>
</template>

<script>
import moment from "moment";
import { api } from "@/api";
import { round10 } from "round10";
import { SalesEditor } from "@/modules/Ventes/components";
export default {
  name: "AnalyticSalesGrid",
  props: {
    data: {
      type: Array,
      required: true
    }
  },
  components: {
    SalesEditor
  },
  data() {
    return {
      page: 1,
      pageCount: 0,
      headers: [
        { value: "date", text: "Date" },
        { value: "sales", text: "Ventes nettes" },
        { value: "net_sales", text: "Ventes après marge de perte" },
        { value: "cost", text: "Cost" },
        { value: "past_year_sales", text: "Année passée" },
        { value: "last_week_sales", text: "Semaine passée" },
        {
          value: "yesterday_sales",
          text: "Journée précédente"
        },
        { value: "actions", text: "" }
      ]
    };
  },
  methods: {
    getDelta(sales, past_sales) {
      if (sales > 0) {
        return round10(((sales - past_sales) / sales) * 100, 0);
      } else {
        return null;
      }
    },
    getDateDisplay(date) {
      return `${date} (${moment(date).isoWeek()}-${moment(date).isoWeekday()})`;
    },
    getPercentCost(item) {
      return round10((item.cost / item.net_sales) * 100, -2);
    },
    getNetRatio(item) {
      if (item.sales !== 0 && item.net_sales !== 0) {
        return round10(((item.sales - item.net_sales) / item.sales) * 100, -2);
      } else {
        return 0;
      }
    },
    async updateCost(item) {
      await api.updateCostFromTransactions({ date: item.date });
      this.$emit("update");
    }
  }
};
</script>

<style></style>
